import * as React from "react";

import { VideoProjectKanbanBoard } from "@/components/VideoProjectKanban";
import { Avatar, Combobox } from "@/components/ui";
import { PlayersServices } from "@/services/bedrock";
import { extractInitials, usePaginatedCombobox } from "@/utils";

import { COACH_KANBAN_COLUMNS } from "./coachKanbanColumns";
import {
  CoachKanbanBoardFilters,
  coachKanbanBoardQueryKey,
  fetchCoachKanbanBoard,
  fetchNextCoachKanbanColumnPage,
} from "./coachKanbanQuery";
import { CoachVideoProjectKanbanCard } from "./CoachVideoProjectKanbanCard";

interface CoachVideoProjectKanbanProps {
  experienceToggle: React.ReactNode;
  sharedActions: React.ReactNode;
}

export function CoachVideoProjectKanban({ experienceToggle, sharedActions }: CoachVideoProjectKanbanProps) {
  const getPlayers = React.useCallback(async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page: page + 1, perPage: 25 });
    return {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: player.fullName || "No Name",
        photoUrl: player.photoUrl,
      })),
    };
  }, []);

  const players = usePaginatedCombobox(getPlayers, false);
  const selectedPlayerId = players.value?.id;

  const boardFilters = React.useMemo<CoachKanbanBoardFilters>(
    () => ({ playerId: selectedPlayerId }),
    [selectedPlayerId]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 items-center flex-wrap justify-between">
        <div className="flex gap-4 items-center flex-wrap flex-auto">
          <Combobox
            className="max-w-80 min-w-50 bg-white"
            placeholder="Filter by Player"
            showClear
            {...players}
            itemToStringLabel={item => item.label}
            itemToIcon={item => (
              <Avatar size="sm" className="mr-1.5">
                <Avatar.Image src={item.photoUrl || undefined} alt="" />
                <Avatar.Fallback className="text-[8px]">{extractInitials(item.label)}</Avatar.Fallback>
              </Avatar>
            )}
          />
          {experienceToggle}
        </div>
        {sharedActions}
      </div>

      <VideoProjectKanbanBoard
        columns={COACH_KANBAN_COLUMNS}
        queryKey={coachKanbanBoardQueryKey(boardFilters)}
        fetchBoard={({ signal }) => fetchCoachKanbanBoard(boardFilters, { signal })}
        fetchNextColumnPage={(column, columnData, signal) =>
          fetchNextCoachKanbanColumnPage(column, boardFilters, columnData, signal)
        }
        renderCard={project => <CoachVideoProjectKanbanCard videoProject={project} />}
      />
    </div>
  );
}
